// The newsletter API's shapes (T-382): what the composer sends to save, and checks shared with the
// Worker. The model itself is ./types.ts.
import { z } from 'zod';
import { contentHash } from '../content-hash';
import {
  DIVIDER_STROKES,
  DIVIDER_THICKNESS,
  DIVIDER_WIDTH_PERCENT,
  FEATURED_PICTURE_SHAPES,
  NEWSLETTER_FONTS,
  SPACER_HEIGHT,
  isHexColour,
  type NewsletterBlock,
} from './types';

export const NEWSLETTER_STALE = 'NEWSLETTER_STALE' as const;
export const NEWSLETTER_SETTINGS_STALE = 'NEWSLETTER_SETTINGS_STALE' as const;

/** Blocks a newsletter can have, and their JSON's size, well inside Shopify's 500 KB for the code. */
export const NEWSLETTER_MAX_BLOCKS = 120;
export const NEWSLETTER_BLOCKS_MAX_BYTES = 400_000;

// Blocks as sent: each one's required fields are checked, so a saved newsletter always opens and builds.
// Extra keys pass (loose objects), so an older or newer composer's additions aren't refused. No counts
// per block: the composer has none, and the newsletter's size limit below bounds them.
const text = z.string().max(20_000);
const imageShape = z.looseObject({ url: z.string().max(2000), alt: z.string().max(1000) });
const linkShape = z.discriminatedUnion('kind', [
  z.looseObject({ kind: z.literal('product'), handle: z.string().max(300) }),
  z.looseObject({ kind: z.literal('collection'), handle: z.string().max(300) }),
  z.looseObject({ kind: z.literal('url'), url: z.string().max(2000) }),
]);
const productShape = z.looseObject({ handle: z.string().min(1).max(300), overrides: z.record(z.string(), z.string().max(5000)).optional() });
const base = { id: z.string().min(1).max(80), style: z.record(z.string(), z.unknown()).optional() };
const blockShape = z.discriminatedUnion('type', [
  z.looseObject({ ...base, type: z.literal('hero'), image: imageShape.nullable(), link: linkShape.optional() }),
  z.looseObject({ ...base, type: z.literal('banner'), image: imageShape.nullable(), link: linkShape.optional() }),
  z.looseObject({
    ...base,
    type: z.literal('section'),
    heading: text.optional(),
    columns: z.union([z.literal(2), z.literal(3)]),
    products: z.array(productShape),
    button: z.looseObject({ label: text, link: linkShape }).optional(),
  }),
  z.looseObject({ ...base, type: z.literal('featured'), product: productShape.nullable(), buttonLabel: text, pictureShape: z.enum(FEATURED_PICTURE_SHAPES).optional() }),
  z.looseObject({
    ...base,
    type: z.literal('images'),
    columns: z.union([z.literal(1), z.literal(2)]),
    items: z.array(z.looseObject({ image: imageShape.nullable(), text: text.optional(), link: linkShape.optional() })),
  }),
  z.looseObject({ ...base, type: z.literal('video'), videoUrl: z.string().max(2000), thumbnail: imageShape.nullable(), thumbnailFromVideo: z.boolean(), caption: text.optional() }),
  z.looseObject({ ...base, type: z.literal('paragraph'), html: z.string().max(100_000) }),
  z.looseObject({ ...base, type: z.literal('heading'), text }),
  z.looseObject({ ...base, type: z.literal('button'), label: text, link: linkShape.nullable() }),
  z.looseObject({
    ...base,
    type: z.literal('divider'),
    stroke: z.enum(DIVIDER_STROKES),
    thickness: z.number().int().min(DIVIDER_THICKNESS.min).max(DIVIDER_THICKNESS.max),
    widthPercent: z.number().int().min(DIVIDER_WIDTH_PERCENT.min).max(DIVIDER_WIDTH_PERCENT.max),
    colour: z.string().max(40).optional(),
    align: z.enum(['left', 'center', 'right']),
  }),
  z.looseObject({ ...base, type: z.literal('spacer'), height: z.number().int().min(SPACER_HEIGHT.min).max(SPACER_HEIGHT.max) }),
]);

const blocksSchema = z
  .array(blockShape)
  .max(NEWSLETTER_MAX_BLOCKS)
  .refine((blocks) => new TextEncoder().encode(JSON.stringify(blocks)).length <= NEWSLETTER_BLOCKS_MAX_BYTES, {
    message: 'The newsletter is too large',
  });

/** Autosave: the whole draft, with the version it started from (stale-write check). */
export const newsletterSaveSchema = z.object({
  subject: z.string().max(300),
  previewText: z.string().max(300),
  blocks: blocksSchema,
  /** Left out (a composer from before T-398), the saved value stays. */
  showHeader: z.boolean().optional(),
  /** The colour round the email (T-59); null follows Newsletter settings. Left out, the saved value stays. */
  pageColour: z.string().refine(isHexColour).nullable().optional(),
  expected_updated_at: z.string().max(40).optional(),
});
export type NewsletterSave = Omit<z.infer<typeof newsletterSaveSchema>, 'blocks'> & { blocks: NewsletterBlock[] };

/** Copy for Shopify: the content that was copied, so "Changed since you copied it" compares with it. */
export const newsletterCopiedSchema = z.object({ copiedHash: z.string().max(64) });

export const newsletterLinkCampaignSchema = z.object({ campaignId: z.string().uuid().nullable() });

const colour = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const image = z.object({ url: z.string().max(2000), alt: z.string().max(300), width: z.number().optional(), height: z.number().optional() }).nullable();

export const newsletterSettingsSchema = z.object({
  settings: z.object({
    storeUrl: z.string().url().max(300),
    logo: image,
    logoWidth: z.number().int().min(60).max(560),
    brandName: z.string().max(120),
    tagline: z.string().max(200),
    address: z.string().max(500),
    phone: z.string().max(60),
    email: z.string().max(200),
    socials: z.array(z.object({ kind: z.enum(['facebook', 'instagram', 'youtube', 'tiktok']), url: z.string().max(500), iconUrl: z.string().max(2000).optional() })).max(4),
    colours: z.object({ page: colour, background: colour, text: colour, muted: colour, accent: colour, accentText: colour, sale: colour }),
    font: z.enum(NEWSLETTER_FONTS),
    headingFont: z.enum(NEWSLETTER_FONTS),
    buttonStyle: z.enum(['filled', 'outline']),
    buttonRadius: z.number().int().min(0).max(30),
    align: z.enum(['left', 'center']),
  }),
  expected_updated_at: z.string().max(40).optional(),
});

/** Newsletter settings as loaded: the team's, or defaults; with the version for the stale-write check. */
export interface NewsletterSettingsResponse {
  settings: import('./types').NewsletterSettings;
  /** Never saved: these are the defaults. */
  isDefault: boolean;
  updatedAt: string | null;
  /** The shop's money format from Shopify, as `| money` uses it when the email is sent. */
  moneyFormat: string | null;
}

/**
 * A fingerprint of a newsletter's content (subject, preview text, blocks, the header when it's hidden,
 * and its own colour round the email when it has one): FNV-1a over its JSON with keys sorted, so the copy
 * saved in the database (whose JSON keys come back in another order) matches the draft it came from. Kept
 * when it's copied; the composer compares the draft's with it. A shown header and the settings' page
 * colour add nothing, so fingerprints from before T-398 and T-59 still match.
 */
export function newsletterContentHash(n: {
  subject: string;
  previewText: string;
  blocks: unknown;
  showHeader?: boolean;
  pageColour?: string | null;
}): string {
  const extra = { ...(n.showHeader === false && { showHeader: false }), ...(n.pageColour && { pageColour: n.pageColour.toLowerCase() }) };
  return contentHash(Object.keys(extra).length ? [n.subject, n.previewText, n.blocks, extra] : [n.subject, n.previewText, n.blocks]);
}
